import React, { useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import { LuLocate, LuMinus, LuNavigation2, LuPlus } from 'react-icons/lu';
import { IconType } from 'react-icons';
import mapboxgl from 'mapbox-gl';

const ICON_SIZE = 16;

function ic(Icon: IconType, size = ICON_SIZE) {
  const C = Icon as React.ComponentType<{ size?: number }>;
  return <C size={size} />;
}

const BTN_BASE =
  'flex h-8 w-8 items-center justify-center rounded-[var(--radius-button)] transition-colors cursor-pointer text-success hover:bg-gray-bright';

const HSep = () => (
  <div className="my-0.5 h-px w-5 self-center bg-gray-200" />
);

// ─────────────────────────────────────────────────────────────
// MapNavToolbar
// Right-side vertical toolbar.
// Replaces native Mapbox NavigationControl + GeolocateControl.
// ─────────────────────────────────────────────────────────────
const MapNavToolbar = ({
  mapRef,
}: {
  mapRef: React.RefObject<mapboxgl.Map | null>;
}) => {
  const [bearing, setBearing] = useState(0);
  const [isLocating, setIsLocating] = useState(false);

  useEffect(() => {
    // Defer to after parent's useEffect has initialised the map
    let attachedMap: mapboxgl.Map | null = null;
    const onRotate = () => {
      if (attachedMap) setBearing(attachedMap.getBearing());
    };
    const timer = setTimeout(() => {
      attachedMap = mapRef.current;
      attachedMap?.on('rotate', onRotate);
    }, 0);
    return () => {
      clearTimeout(timer);
      attachedMap?.off('rotate', onRotate);
    };
  }, [mapRef]);

  const handleZoomIn = () => mapRef.current?.zoomIn();
  const handleZoomOut = () => mapRef.current?.zoomOut();
  const handleResetNorth = () => mapRef.current?.resetNorth({ duration: 0 });

  const handleGeolocate = () => {
    if (!navigator.geolocation || isLocating) return;
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        mapRef.current?.flyTo({
          center: [pos.coords.longitude, pos.coords.latitude],
          zoom: 15,
          duration: 0,
        });
        setIsLocating(false);
      },
      () => setIsLocating(false),
      { timeout: 10_000 }
    );
  };

  return (
    <div
      className="absolute right-2 z-10 flex flex-col items-center rounded-xl bg-white px-1.5 py-1.5 bottom-9 map-card-shadow"
    >
      <Tooltip title="Zoom in" placement="left">
        <button
          type="button"
          className={BTN_BASE}
          onClick={handleZoomIn}
          aria-label="Zoom in"
        >
          {ic(LuPlus)}
        </button>
      </Tooltip>

      <Tooltip title="Zoom out" placement="left">
        <button
          type="button"
          className={BTN_BASE}
          onClick={handleZoomOut}
          aria-label="Zoom out"
        >
          {ic(LuMinus)}
        </button>
      </Tooltip>

      <HSep />

      <Tooltip title="Reset north" placement="left">
        <button
          type="button"
          className={BTN_BASE}
          onClick={handleResetNorth}
          aria-label="Reset north"
          style={{ transform: `rotate(${-bearing}deg)`, transition: 'transform 0.3s ease' }}
        >
          {ic(LuNavigation2)}
        </button>
      </Tooltip>

      <HSep />

      <Tooltip title={isLocating ? 'Locating…' : 'My location'} placement="left">
        <button
          type="button"
          className={`${BTN_BASE}${isLocating ? ' opacity-60' : ''}`}
          onClick={handleGeolocate}
          aria-label="My location"
          disabled={isLocating}
        >
          {ic(LuLocate)}
        </button>
      </Tooltip>
    </div>
  );
};

export default MapNavToolbar;
